// Pre-open readiness check button for the System workspace header (2026-10-05).
// One click runs shared/preopen_check.py on the backend (read-only: it places no order and writes nothing) and shows, per book (MAIN, Tier 2), whether
// anything would stop a signal from becoming an order at the open: kill switches, breakers, capital tier, Alpaca accounts and the paper wall, data
// freshness, the fitted universe, worker liveness. READY does not prove a signal will fire. The same logic runs from a terminal:
//   python scripts/preopen_check.py   (needs SAFERFUNDS_DATABASE_URL; on Railway: railway run -s saferfunds python scripts/preopen_check.py)
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { ClipboardCheck, RefreshCw, X } from 'lucide-react'
import { preopen } from '../lib/api'

const MONO = { fontFamily: 'JetBrains Mono, monospace' }
const TONE = {
  PASS: { c: '#4ade80', label: 'PASS' },
  WARN: { c: '#fbbf24', label: 'WARN' },
  FAIL: { c: '#f87171', label: 'FAIL' },
  SKIP: { c: '#64748b', label: 'SKIP' },
}
const VERDICT = {
  READY: { c: '#4ade80', text: 'READY' },
  READY_WITH_WARNINGS: { c: '#fbbf24', text: 'READY, WARNINGS' },
  NOT_READY: { c: '#f87171', text: 'NOT READY' },
}
const GROUPS = [['SHARED', 'Shared (both books)'], ['MAIN', 'MAIN'], ['TIER2', 'Tier 2']]

function Chip({ label, verdict }) {
  const v = VERDICT[verdict] || { c: '#64748b', text: '...' }
  return (
    <span style={{ ...MONO, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.05em', color: v.c, border: `1px solid ${v.c}66`, background: `${v.c}14`, borderRadius: 6, padding: '3px 8px' }}>
      {label}: {v.text}
    </span>
  )
}

export default function PreopenCheck() {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const box = useRef(null)

  const run = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await preopen.run()
      if (r.success) setData(r); else setError(r.error || 'the check failed')
    } catch (e) { setError(String(e.message || e)) }
    finally { setLoading(false) }
  }, [])

  const toggle = () => { if (!open) { setOpen(true); run() } else setOpen(false) }

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    const onDown = (e) => { if (box.current && !box.current.contains(e.target) && !e.target.closest('[data-preopen-toggle]')) setOpen(false) }
    window.addEventListener('keydown', onKey); document.addEventListener('mousedown', onDown)
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown) }
  }, [open])

  const dot = data ? (VERDICT[data.verdict] || {}).c : '#64748b'
  return (
    <span style={{ position: 'relative' }} data-preopen-toggle="1">
      <button onClick={toggle} aria-expanded={open} aria-label="Pre-open check"
        title="Checks that nothing will stop MAIN and Tier 2 from placing orders at the open. Read-only."
        style={{ ...MONO, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.06em', color: '#7dd3fc', border: '1px solid rgba(56,189,248,0.45)',
                 background: open ? 'rgba(56,189,248,0.18)' : 'rgba(56,189,248,0.07)', borderRadius: 6, padding: '4px 9px', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        <ClipboardCheck className="h-3.5 w-3.5" />
        PRE-OPEN CHECK
        {data && <span style={{ width: 8, height: 8, borderRadius: 4, background: dot, display: 'inline-block' }} />}
      </button>
      {open && (
        <div ref={box} role="dialog" aria-label="Pre-open check"
          style={{ position: 'fixed', top: 96, right: 12, zIndex: 70, width: 'min(640px, calc(100vw - 24px))', maxHeight: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column',
                   background: '#0f172a', border: '1px solid #334155', borderRadius: 12, boxShadow: '0 16px 48px rgba(0,0,0,0.55)' }}>
          <div className="flex items-center gap-2" style={{ padding: '10px 12px', borderBottom: '1px solid #1e293b', flexWrap: 'wrap' }}>
            <ClipboardCheck className="h-4 w-4" style={{ color: '#7dd3fc' }} />
            <strong style={{ fontSize: '0.9rem', color: '#e2e8f0' }}>Pre-open check</strong>
            {data && <><Chip label="MAIN" verdict={data.main} /><Chip label="TIER 2" verdict={data.tier2} /></>}
            <span style={{ flex: 1 }} />
            <button onClick={run} disabled={loading} aria-label="Run again" title="Run again"
              style={{ ...MONO, fontSize: '0.64rem', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, padding: '3px 8px', display: 'inline-flex', alignItems: 'center', gap: 5, cursor: loading ? 'wait' : 'pointer' }}>
              <RefreshCw className="h-3 w-3" style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />{loading ? 'RUNNING' : 'RUN AGAIN'}
            </button>
            <button onClick={() => setOpen(false)} aria-label="Close" style={{ color: '#64748b' }}><X className="h-4 w-4" /></button>
          </div>
          <div style={{ overflowY: 'auto', padding: '8px 12px 12px' }}>
            {error && <div style={{ ...MONO, fontSize: '0.72rem', color: '#f87171', padding: '8px 0' }}>Could not run the check: {error}</div>}
            {!data && !error && <div style={{ ...MONO, fontSize: '0.72rem', color: '#64748b', padding: '8px 0' }}>Running...</div>}
            {data && GROUPS.map(([g, title]) => (
              <div key={g} style={{ marginTop: 8 }}>
                <div style={{ ...MONO, fontSize: '0.64rem', color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>{title}</div>
                {data.checks.filter(c => c.group === g).map(c => {
                  const t = TONE[c.status] || TONE.SKIP
                  return (
                    <div key={c.id} style={{ display: 'flex', gap: 8, padding: '4px 0', borderTop: '1px solid #16213a' }}>
                      <span style={{ ...MONO, fontSize: '0.62rem', fontWeight: 700, color: t.c, minWidth: 34 }}>{t.label}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '0.78rem', color: '#e2e8f0' }}>{c.name}</div>
                        <div style={{ ...MONO, fontSize: '0.66rem', color: '#94a3b8', overflowWrap: 'anywhere' }}>{c.detail}</div>
                        {c.fix && (c.status === 'WARN' || c.status === 'FAIL') && <div style={{ ...MONO, fontSize: '0.64rem', color: t.c, marginTop: 1 }}>-&gt; {c.fix}</div>}
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}
            {data && (
              <div style={{ ...MONO, fontSize: '0.62rem', color: '#64748b', marginTop: 10, lineHeight: 1.5 }}>
                {data.counts.PASS} pass, {data.counts.WARN} warn, {data.counts.FAIL} fail, {data.counts.SKIP} skipped. READY means nothing will stop a signal from becoming an order; it does not prove a signal will fire.
                Same check from a terminal: python scripts/preopen_check.py (Linux, with the engine environment)
              </div>
            )}
          </div>
        </div>
      )}
    </span>
  )
}
